import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useQuery, useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import { FormattedMessage, useIntl } from 'react-intl';
import Input from '../../../../../../ui/canon/Input';
import Badge from '../../../../../../ui/canon/Badge';
import StatusIndicator from '../../../../../../ui/canon/StatusIndicator';
import AlertCard from '../../../../../../ui/canon/AlertCard';

// Add slider thumb styling
const sliderStyle = document.createElement('style');
sliderStyle.textContent = `
  .slider::-webkit-slider-thumb {
    appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: var(--succession-primary);
    cursor: pointer;
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  }
  .slider::-moz-range-thumb {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: var(--succession-primary);
    cursor: pointer;
    border: none;
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  }
`;
if (typeof document !== 'undefined' && !document.getElementById('slider-thumb-style')) {
  sliderStyle.id = 'slider-thumb-style';
  document.head.appendChild(sliderStyle);
}

const GET_USER_NEARBY_COUNT = gql`
  query getUserNearbyCount($proximityMax: Int) {
    getUserNearbyCount(proximityMax: $proximityMax)
  }
`;

const UPDATE_MATCH_PREFERENCE = gql`
  mutation updateMatchPreference(
    $affiliation: Int
    $practice_similarity: Int
    $practice_structure: Int
    $servicing: Int
    $length_of_service: Int
    $length_of_service_min: Int
    $length_of_service_max: Int
    $aum: Int
    $aum_min: Float
    $aum_max: Float
    $avg_age_of_client: Int
    $avg_age_of_client_min: Float
    $avg_age_of_client_max: Float
    $proximity_max: Int
    $affiliations: Int
  ) {
    updateMatchPreference(
      affiliation: $affiliation
      practice_similarity: $practice_similarity
      practice_structure: $practice_structure
      servicing: $servicing
      length_of_service: $length_of_service
      length_of_service_min: $length_of_service_min
      length_of_service_max: $length_of_service_max
      aum: $aum
      aum_min: $aum_min
      aum_max: $aum_max
      avg_age_of_client: $avg_age_of_client
      avg_age_of_client_min: $avg_age_of_client_min
      avg_age_of_client_max: $avg_age_of_client_max
      proximity_max: $proximity_max
      affiliations: $affiliations
    )
  }
`;

// Premium Interactive Question Component
const PremiumMatchQuestion = React.memo(({ 
  icon, 
  title, 
  description, 
  selection, 
  options, 
  onChange, 
  showRangeInputs = false,
  rangeInputs = null,
  factorLabels = {
    skip: "Skip this factor",
    consider: "Consider this factor", 
    prioritize: "Prioritize this factor"
  },
  finetuneLabel = "Fine-tune your preferences"
}) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div 
      className="bg-gradient-to-br from-succession-bg-primary to-succession-bg-secondary border border-succession-border rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-500 hover:scale-[1.02] group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Question Header */}
      <div className="flex items-start space-x-4 mb-6">
        <div className="flex-1">
          <h3 className="text-xl font-bold text-succession-text-primary mb-2 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors duration-300">
            {title}
          </h3>
          <p className="text-sm text-succession-text-secondary leading-relaxed">
            {description}
          </p>
        </div>
      </div>

      {/* Interactive Options */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {options.map((option, index) => {
          const isSelected = selection === option.value;
          const colors = [
            'from-succession-primary-10 to-succession-bg-secondary border-succession-primary text-succession-primary shadow-succession',
            'from-succession-primary-10 to-succession-bg-secondary border-succession-primary text-succession-primary shadow-succession',
            'from-succession-primary-10 to-succession-bg-secondary border-succession-primary text-succession-primary shadow-succession',
          ];
          const selectedColors = [
            'from-succession-primary-10 to-succession-bg-secondary border-succession-primary text-succession-primary shadow-succession-lg ring-succession-primary',
            'from-succession-primary-10 to-succession-bg-secondary border-succession-primary text-succession-primary shadow-succession-lg ring-succession-primary',
            'from-succession-primary-10 to-succession-bg-secondary border-succession-primary text-succession-primary shadow-succession-lg ring-succession-primary',
          ];

          return (
            <button
              key={option.value}
              onClick={() => onChange(option.value)}
              className={`relative p-4 rounded-xl border-2 transition-all duration-300 transform hover:scale-105 hover:shadow-lg ${
                isSelected 
                  ? `bg-gradient-to-br ${selectedColors[index]} ring-4 ring-opacity-30` 
                  : `bg-gradient-to-br ${colors[index]} hover:shadow-md`
              }`}
            >
              <div className="text-lg font-semibold mb-1">{option.label}</div>
              <div className="text-xs font-medium opacity-70">
                {index === 0 && factorLabels.skip}
                {index === 1 && factorLabels.consider}
                {index === 2 && factorLabels.prioritize}
              </div>
              {isSelected && (
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-white dark:bg-succession-bg-primary rounded-full flex items-center justify-center shadow-lg">
                  <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Range Inputs */}
      {showRangeInputs && selection !== '0' && rangeInputs && (
        <div className="transform transition-all duration-500 ease-out translate-y-0 opacity-100">
          <div className="bg-succession-bg-primary border-2 border-succession-primary/20 rounded-xl p-6 shadow-inner">
            {rangeInputs}
          </div>
        </div>
      )}
    </div>
  );
});

const MatchPreferences = ({ formData, updateFormData, errors, currentUser, company }) => {
  const intl = useIntl();

  // Helper function to detect if client is Canadian
  const isCanadianClient = () => {
    console.log('MatchPreferences - company:', company);
    return company?.country === 'ca';
  };

  // Helper function to convert miles to kilometers
  const milesToKm = (miles) => {
    return Math.round(miles * 1.60934);
  };

  // Helper function to map weight values to survey scale (like original MatchSurvey.jsx)
  const mapValueToSurvey = (val) => {
    if (val === null || val === undefined) return null;
    if (val > 75) {
      return '100';
    }
    if (val < 25) {
      return '0';
    }
    return '50';
  };

  // Initialize state from user's existing match preferences (like original implementation)
  const matchPref = currentUser?.match_preference;
  
  // Debug logging
  console.log('MatchPreferences - currentUser:', currentUser);
  console.log('MatchPreferences - matchPref:', matchPref);
  
  const [preferences, setPreferences] = useState({
    proximity_max: matchPref?.proximity_max || 150,
    affiliation: matchPref ? mapValueToSurvey(matchPref.affiliation_weight) : '50',
    affiliations: matchPref ? mapValueToSurvey(matchPref.affiliate_network_weight) : '50',
    practice_similarity: matchPref ? mapValueToSurvey(matchPref.product_mix_weight) : '50',
    practice_structure: matchPref ? mapValueToSurvey(matchPref.part_of_team_weight) : '50',
    servicing: matchPref ? mapValueToSurvey(matchPref.distance_weight) : '50',
    length_of_service: matchPref ? mapValueToSurvey(matchPref.length_of_service_weight) : '50',
    length_of_service_min: matchPref?.length_of_service_min || null,
    length_of_service_max: matchPref?.length_of_service_max || null,
    aum: matchPref ? mapValueToSurvey(matchPref.aum_weight) : '50',
    aum_min: matchPref?.aum_min || null,
    aum_max: matchPref?.aum_max || null,
    avg_age_of_client: matchPref ? mapValueToSurvey(matchPref.avg_age_of_client_weight) : '50',
    avg_age_of_client_min: matchPref?.avg_age_of_client_min || null,
    avg_age_of_client_max: matchPref?.avg_age_of_client_max || null,
  });

  const [proximityMessage, setProximityMessage] = useState('0');

  // Query for nearby user count
  const { data: proximityData, loading: proximityLoading } = useQuery(GET_USER_NEARBY_COUNT, {
    variables: { proximityMax: parseInt(preferences.proximity_max) },
    skip: !preferences.proximity_max,
    fetchPolicy: 'cache-and-network', // Always fetch fresh data
    onCompleted: (data) => {
      const count = data.getUserNearbyCount || '0';
      console.log('Proximity query completed, count:', count);
      setProximityMessage(count);
    },
    onError: (error) => {
      console.error('Proximity query error:', error);
      setProximityMessage('0');
    }
  });

  // Mutation for updating match preferences
  const [updateMatchPreferenceMutation] = useMutation(UPDATE_MATCH_PREFERENCE, {
    onCompleted: (data) => {
      console.log('Match preferences updated successfully');
    },
    onError: (error) => {
      console.error('Error updating match preferences:', error);
    }
  });

  // Function to save match preferences to database
  const saveMatchPreferences = async (newPreferences) => {
    try {
      // Convert survey values (0/50/100) back to weight values (0/50/100) 
      // The mutation expects Int values matching the survey scale
      await updateMatchPreferenceMutation({
        variables: {
          affiliation: newPreferences.affiliation ? parseInt(newPreferences.affiliation) : null,
          practice_similarity: newPreferences.practice_similarity ? parseInt(newPreferences.practice_similarity) : null,
          practice_structure: newPreferences.practice_structure ? parseInt(newPreferences.practice_structure) : null,
          servicing: newPreferences.servicing ? parseInt(newPreferences.servicing) : null,
          length_of_service: newPreferences.length_of_service ? parseInt(newPreferences.length_of_service) : null,
          length_of_service_min: newPreferences.length_of_service_min ? parseInt(newPreferences.length_of_service_min) : null,
          length_of_service_max: newPreferences.length_of_service_max ? parseInt(newPreferences.length_of_service_max) : null,
          aum: newPreferences.aum ? parseInt(newPreferences.aum) : null,
          aum_min: newPreferences.aum_min ? parseFloat(newPreferences.aum_min) : null,
          aum_max: newPreferences.aum_max ? parseFloat(newPreferences.aum_max) : null,
          avg_age_of_client: newPreferences.avg_age_of_client ? parseInt(newPreferences.avg_age_of_client) : null,
          avg_age_of_client_min: newPreferences.avg_age_of_client_min ? parseFloat(newPreferences.avg_age_of_client_min) : null,
          avg_age_of_client_max: newPreferences.avg_age_of_client_max ? parseFloat(newPreferences.avg_age_of_client_max) : null,
          proximity_max: newPreferences.proximity_max ? parseInt(newPreferences.proximity_max) : null,
          affiliations: newPreferences.affiliations ? parseInt(newPreferences.affiliations) : null,
        }
      });
    } catch (error) {
      console.error('Failed to save match preferences:', error);
    }
  };

  // Check if preferences are filled out sufficiently
  const isPreferencesComplete = () => {
    // Check if proximity is set
    if (!preferences.proximity_max || preferences.proximity_max <= 0) {
      return false;
    }
    
    // Check if all visible questions are answered
    const requiredQuestions = ['practice_similarity', 'practice_structure', 'servicing', 'length_of_service'];
    
    // Add AUM if not P&C business type
    if (company?.business_type !== 'p_and_c') {
      requiredQuestions.push('aum');
    }
    
    // Add avg_age_of_client for all business types
    requiredQuestions.push('avg_age_of_client');
    
    // Add affiliation questions if they should be shown
    if (company?.has_affiliation) {
      requiredQuestions.push('affiliation');
    }
    if (company?.allow_user_affiliations) {
      requiredQuestions.push('affiliations');
    }
    
    // Check if all required questions have been answered
    const allQuestionsAnswered = requiredQuestions.every(question => 
      preferences[question] !== null && preferences[question] !== undefined
    );
    
    if (!allQuestionsAnswered) {
      return false;
    }
    
    // Check range inputs for questions that are not set to "Not Important" (0)
    
    // Length of Service range validation
    if (preferences.length_of_service && preferences.length_of_service !== '0') {
      if (preferences.length_of_service_min === null || preferences.length_of_service_min === undefined || 
          preferences.length_of_service_max === null || preferences.length_of_service_max === undefined) {
        return false;
      }
    }
    
    // AUM range validation (only if AUM question is visible)
    if (company?.business_type !== 'p_and_c' && preferences.aum && preferences.aum !== '0') {
      if (preferences.aum_min === null || preferences.aum_min === undefined || 
          preferences.aum_max === null || preferences.aum_max === undefined) {
        return false;
      }
    }
    
    // Average Age of Client range validation
    if (preferences.avg_age_of_client && preferences.avg_age_of_client !== '0') {
      if (preferences.avg_age_of_client_min === null || preferences.avg_age_of_client_min === undefined || 
          preferences.avg_age_of_client_max === null || preferences.avg_age_of_client_max === undefined) {
        return false;
      }
    }
    
    return true;
  };

  // Update form data when preferences change, including validation
  useEffect(() => {
    const preferencesComplete = isPreferencesComplete();
    const finalValidation = preferencesComplete && !proximityLoading && proximityMessage !== '0' && proximityMessage !== '';
    
    console.log('MatchPreferences validation check:', {
      isPreferencesComplete: preferencesComplete,
      proximityMessage,
      proximityLoading,
      proximityMax: preferences.proximity_max,
      finalValidation,
      preferences: {
        length_of_service: preferences.length_of_service,
        length_of_service_min: preferences.length_of_service_min,
        length_of_service_max: preferences.length_of_service_max,
        aum: preferences.aum,
        aum_min: preferences.aum_min,
        aum_max: preferences.aum_max,
        avg_age_of_client: preferences.avg_age_of_client,
        avg_age_of_client_min: preferences.avg_age_of_client_min,
        avg_age_of_client_max: preferences.avg_age_of_client_max,
      }
    });

    const updatedData = {
      ...preferences,
      // Don't validate match count while loading, and require proximity_max and non-zero matches
      match_preferences_complete: finalValidation
    };
    updateFormData(updatedData);
  }, [preferences, proximityMessage, proximityLoading]);

  // Save match preferences when they change (debounced)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      saveMatchPreferences(preferences);
    }, 1000); // Debounce for 1 second

    return () => clearTimeout(timeoutId);
  }, [preferences]);

  const handlePreferenceChange = useCallback((field, value) => {
    setPreferences(prev => ({ ...prev, [field]: value }));
    
    // Reset proximity message when proximity_max changes to avoid stale data
    if (field === 'proximity_max') {
      console.log('Proximity max changed to:', value);
      setProximityMessage('0'); // Reset to 0 while new query is loading
    }
  }, []);



  const formatCurrency = useCallback((value) => {
    if (!value || value === 0) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }, []);

  // Create memoized change handlers for each field to prevent re-renders
  const handleAffiliationChange = useCallback((value) => handlePreferenceChange('affiliation', value), [handlePreferenceChange]);
  const handleAffiliationsChange = useCallback((value) => handlePreferenceChange('affiliations', value), [handlePreferenceChange]);
  const handlePracticeSimilarityChange = useCallback((value) => handlePreferenceChange('practice_similarity', value), [handlePreferenceChange]);
  const handlePracticeStructureChange = useCallback((value) => handlePreferenceChange('practice_structure', value), [handlePreferenceChange]);
  const handleServicingChange = useCallback((value) => handlePreferenceChange('servicing', value), [handlePreferenceChange]);
  const handleLengthOfServiceChange = useCallback((value) => handlePreferenceChange('length_of_service', value), [handlePreferenceChange]);
  const handleAumChange = useCallback((value) => handlePreferenceChange('aum', value), [handlePreferenceChange]);
  const handleAvgAgeOfClientChange = useCallback((value) => handlePreferenceChange('avg_age_of_client', value), [handlePreferenceChange]);

  // Memoized handlers for number inputs - only allow numbers
  const handleProximityMaxChange = useCallback((e) => handlePreferenceChange('proximity_max', parseInt(e.target.value, 10)), [handlePreferenceChange]);
  const handleLengthOfServiceMinChange = useCallback((e) => {
    const value = e.target.value.replace(/[^\d]/g, ''); // Only allow digits
    handlePreferenceChange('length_of_service_min', value ? parseInt(value, 10) : null);
  }, [handlePreferenceChange]);
  const handleLengthOfServiceMaxChange = useCallback((e) => {
    const value = e.target.value.replace(/[^\d]/g, ''); // Only allow digits
    handlePreferenceChange('length_of_service_max', value ? parseInt(value, 10) : null);
  }, [handlePreferenceChange]);
  const handleAvgAgeOfClientMinChange = useCallback((e) => {
    const value = e.target.value.replace(/[^\d]/g, ''); // Only allow digits
    handlePreferenceChange('avg_age_of_client_min', value ? parseInt(value, 10) : null);
  }, [handlePreferenceChange]);
  const handleAvgAgeOfClientMaxChange = useCallback((e) => {
    const value = e.target.value.replace(/[^\d]/g, ''); // Only allow digits
    handlePreferenceChange('avg_age_of_client_max', value ? parseInt(value, 10) : null);
  }, [handlePreferenceChange]);

  // State for tracking focused AUM inputs
  const [focusedAumField, setFocusedAumField] = useState(null);

  // Memoized handlers for currency inputs  
  const handleAumMinChange = useCallback((e) => {
    const value = e.target.value.replace(/[^\d]/g, ''); // Only allow digits
    handlePreferenceChange('aum_min', value ? parseInt(value, 10) : null);
  }, [handlePreferenceChange]);
  const handleAumMaxChange = useCallback((e) => {
    const value = e.target.value.replace(/[^\d]/g, ''); // Only allow digits
    handlePreferenceChange('aum_max', value ? parseInt(value, 10) : null);
  }, [handlePreferenceChange]);

  // Handlers for AUM field focus/blur
  const handleAumMinFocus = useCallback(() => setFocusedAumField('aum_min'), []);
  const handleAumMinBlur = useCallback(() => setFocusedAumField(null), []);
  const handleAumMaxFocus = useCallback(() => setFocusedAumField('aum_max'), []);
  const handleAumMaxBlur = useCallback(() => setFocusedAumField(null), []);

  const importanceOptions = [
    { 
      value: '0', 
      label: intl.formatMessage({ id: 'succession_deals.wizard.steps.match_preferences.importance_options.not_important' })
    },
    { 
      value: '50', 
      label: intl.formatMessage({ id: 'succession_deals.wizard.steps.match_preferences.importance_options.somewhat_important' })
    },
    { 
      value: '100', 
      label: intl.formatMessage({ id: 'succession_deals.wizard.steps.match_preferences.importance_options.very_important' })
    },
  ];

  const factorLabels = {
    skip: intl.formatMessage({ id: 'succession_deals.wizard.steps.match_preferences.factor_labels.skip' }),
    consider: intl.formatMessage({ id: 'succession_deals.wizard.steps.match_preferences.factor_labels.consider' }),
    prioritize: intl.formatMessage({ id: 'succession_deals.wizard.steps.match_preferences.factor_labels.prioritize' }),
  };

  const finetuneLabel = intl.formatMessage({ id: 'succession_deals.wizard.steps.match_preferences.finetune_label' });

  return (
    <div className="space-y-12">
      {/* Stunning Header */}
      <div className="text-center relative">
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 dark:from-slate-800 dark:via-slate-850 dark:to-slate-900 rounded-3xl opacity-50 dark:opacity-30"></div>
        <div className="relative py-12 px-8">
          <h2 className="text-4xl font-bold text-succession-text-primary mb-4">
            <FormattedMessage id="succession_deals.wizard.steps.match_preferences.title" />
          </h2>
          <p className="text-xl text-succession-text-secondary max-w-3xl mx-auto leading-relaxed">
            <FormattedMessage id="succession_deals.wizard.steps.match_preferences.description" />
          </p>
          <div className="mt-6 flex justify-center flex-wrap gap-2">
            <Badge variant="info">
              <FormattedMessage id="succession_deals.wizard.steps.match_preferences.ai_powered" />
            </Badge>
            <Badge variant="primary">
              <FormattedMessage id="succession_deals.wizard.steps.match_preferences.precision_matching" />
            </Badge>
            <Badge variant="accent">
              <FormattedMessage id="succession_deals.wizard.steps.match_preferences.smart_algorithm" />
            </Badge>
          </div>
        </div>
      </div>

      {/* Premium Location Selector */}
      <div className="bg-gradient-to-br from-succession-bg-primary to-succession-bg-secondary border-2 border-succession-border rounded-3xl p-8 shadow-xl">
        <div className="flex items-start space-x-6">
          <div className="flex-1">
            <h3 className="text-2xl font-bold text-succession-text-primary mb-3">
              <FormattedMessage id="succession_deals.wizard.steps.match_preferences.geographic_range.title" />
            </h3>
            <p className="text-succession-text-secondary mb-6">
              <FormattedMessage id="succession_deals.wizard.steps.match_preferences.geographic_range.description" />
            </p>
            
            <div className="bg-succession-bg-primary rounded-xl p-6 shadow-lg border border-succession-border">
              <div className="flex items-center space-x-4 mb-4">
                <div className="flex-1">
                  <label className="block text-sm font-semibold text-succession-text-primary mb-2">
                    <FormattedMessage id="succession_deals.wizard.steps.match_preferences.geographic_range.max_distance_label" />
                  </label>
                  <input
                    type="range"
                    min="25"
                    max="4000"
                    step="25"
                    value={preferences.proximity_max}
                    onChange={(e) => handlePreferenceChange('proximity_max', parseInt(e.target.value, 10))}
                    className="w-full h-3 bg-succession-border rounded-lg appearance-none cursor-pointer slider"
                    style={{
                      background: `linear-gradient(to right, var(--succession-primary) 0%, var(--succession-primary) ${((preferences.proximity_max - 25) / (4000 - 25)) * 100}%, rgb(229, 231, 235) ${((preferences.proximity_max - 25) / (4000 - 25)) * 100}%, rgb(229, 231, 235) 100%)`
                    }}
                  />
                  <div className="flex justify-between text-xs text-succession-text-muted mt-1">
                    <span>{isCanadianClient() ? `${milesToKm(25)}km` : '25mi'}</span>
                    <span>{isCanadianClient() ? `${milesToKm(4000)}km` : '4000mi'}</span>
                  </div>
                </div>
                <div className="text-center">
                  <div className="bg-succession-primary text-white px-4 py-2 rounded-lg font-bold text-lg min-w-[80px]">
                    {preferences.proximity_max}
                  </div>
                  <div className="text-xs text-succession-text-muted mt-1">
                    {isCanadianClient() ? 'km' : <FormattedMessage id="succession_deals.wizard.steps.match_preferences.geographic_range.miles_label" />}
                  </div>
                </div>
              </div>
              
              <StatusIndicator
                status={proximityLoading ? 'loading' : (proximityMessage === '0' ? 'error' : 'success')}
                message={
                  proximityLoading ? (
                    <FormattedMessage id="succession_deals.wizard.steps.match_preferences.geographic_range.searching_matches" />
                  ) : (
                    <FormattedMessage 
                      id="succession_deals.wizard.steps.match_preferences.geographic_range.matches_found_success"
                      values={{ count: proximityMessage || '0', distance: preferences.proximity_max }}
                    />
                  )
                }
              />
            </div>
          </div>
        </div>
      </div>

      {/* Premium Questions */}
      <div className="space-y-8">
        {/* Practice Similarity */}
        <PremiumMatchQuestion
          title={intl.formatMessage({ id: 'succession_deals.wizard.steps.match_preferences.practice_similarity.title' })}
          description={intl.formatMessage({ id: 'succession_deals.wizard.steps.match_preferences.practice_similarity.description' })}
          selection={preferences.practice_similarity}
          options={importanceOptions}
          onChange={handlePracticeSimilarityChange}
          factorLabels={factorLabels}
          finetuneLabel={finetuneLabel}
        />

        {/* Practice Structure */}
        <PremiumMatchQuestion
          title={intl.formatMessage({ id: 'succession_deals.wizard.steps.match_preferences.practice_structure.title' })}
          description={intl.formatMessage({ id: 'succession_deals.wizard.steps.match_preferences.practice_structure.description' })}
          selection={preferences.practice_structure}
          options={importanceOptions}
          onChange={handlePracticeStructureChange}
          factorLabels={factorLabels}
          finetuneLabel={finetuneLabel}
        />

        {/* Servicing Demographics */}
        <PremiumMatchQuestion
          title={intl.formatMessage({ id: 'succession_deals.wizard.steps.match_preferences.client_demographics.title' })}
          description={intl.formatMessage({ id: 'succession_deals.wizard.steps.match_preferences.client_demographics.description' })}
          selection={preferences.servicing}
          options={importanceOptions}
          onChange={handleServicingChange}
          factorLabels={factorLabels}
          finetuneLabel={finetuneLabel}
        />

        {/* Length of Service */}
        <PremiumMatchQuestion
          title={intl.formatMessage({ id: 'succession_deals.wizard.steps.match_preferences.experience_level.title' })}
          description={intl.formatMessage({ id: 'succession_deals.wizard.steps.match_preferences.experience_level.description' })}
          selection={preferences.length_of_service}
          options={importanceOptions}
          onChange={handleLengthOfServiceChange}
          factorLabels={factorLabels}
          finetuneLabel={finetuneLabel}
          showRangeInputs={true}
          rangeInputs={preferences.length_of_service !== '0' && (
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-succession-text-primary mb-2">
                  <FormattedMessage id="succession_deals.wizard.steps.match_preferences.experience_level.min_years_label" />
                </label>
                <Input
                  type="text"
                  value={preferences.length_of_service_min || ''}
                  onChange={handleLengthOfServiceMinChange}
                  className="px-4 py-3 border-2 border-succession-border rounded-lg shadow-sm focus:ring-2 focus:ring-succession-primary focus:border-succession-primary transition-all duration-200"
                  placeholder={intl.formatMessage({ id: 'succession_deals.wizard.steps.match_preferences.experience_level.min_placeholder' })}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-succession-text-primary mb-2">
                  <FormattedMessage id="succession_deals.wizard.steps.match_preferences.experience_level.max_years_label" />
                </label>
                <Input
                  type="text"
                  value={preferences.length_of_service_max || ''}
                  onChange={handleLengthOfServiceMaxChange}
                  className="px-4 py-3 border-2 border-succession-border rounded-lg shadow-sm focus:ring-2 focus:ring-succession-primary focus:border-succession-primary transition-all duration-200"
                  placeholder={intl.formatMessage({ id: 'succession_deals.wizard.steps.match_preferences.experience_level.max_placeholder' })}
                />
              </div>
            </div>
          )}
        />

        {/* Assets Under Management - Only show for non-P&C business types */}
        {company?.business_type !== 'p_and_c' && (
            <PremiumMatchQuestion
              title={intl.formatMessage({ id: 'succession_deals.wizard.steps.match_preferences.aum.title' })}
              description={intl.formatMessage({ id: 'succession_deals.wizard.steps.match_preferences.aum.description' })}
              selection={preferences.aum}
              options={importanceOptions}
              onChange={handleAumChange}
              factorLabels={factorLabels}
              finetuneLabel={finetuneLabel}
              showRangeInputs={true}
              rangeInputs={preferences.aum !== '0' && (
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-succession-text-primary mb-2">
                    <FormattedMessage id="succession_deals.wizard.steps.match_preferences.aum.min_aum_label" />
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                      <span className="text-succession-primary font-semibold text-sm">$</span>
                    </div>
                    <Input
                      type="text"
                      value={focusedAumField === 'aum_min' ? (preferences.aum_min || '') : (preferences.aum_min ? preferences.aum_min.toLocaleString() : '')}
                      onChange={handleAumMinChange}
                      onFocus={handleAumMinFocus}
                      onBlur={handleAumMinBlur}
                      className="text-right pl-12 pr-4 py-3 border-2 border-succession-border rounded-lg shadow-sm focus:ring-2 focus:ring-succession-primary focus:border-succession-primary transition-all duration-200 font-medium"
                      placeholder={focusedAumField === 'aum_min' ? 
                        intl.formatMessage({ id: 'succession_deals.wizard.steps.match_preferences.aum.min_aum_placeholder' }).replace(/,/g, '') : 
                        intl.formatMessage({ id: 'succession_deals.wizard.steps.match_preferences.aum.min_aum_placeholder' })}
                    />
                  </div>
                  <div className="mt-1 text-xs text-succession-primary">
                    <FormattedMessage id={focusedAumField === 'aum_min' ? 
                      'succession_deals.wizard.steps.match_preferences.aum.numbers_only_help' : 
                      'succession_deals.wizard.steps.match_preferences.aum.click_to_edit'} />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-succession-text-primary mb-2">
                    <FormattedMessage id="succession_deals.wizard.steps.match_preferences.aum.max_aum_label" />
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                      <span className="text-succession-primary font-semibold text-sm">$</span>
                    </div>
                    <Input
                      type="text"
                      value={focusedAumField === 'aum_max' ? (preferences.aum_max || '') : (preferences.aum_max ? preferences.aum_max.toLocaleString() : '')}
                      onChange={handleAumMaxChange}
                      onFocus={handleAumMaxFocus}
                      onBlur={handleAumMaxBlur}
                      className="text-right pl-12 pr-4 py-3 border-2 border-succession-border rounded-lg shadow-sm focus:ring-2 focus:ring-succession-primary focus:border-succession-primary transition-all duration-200 font-medium"
                      placeholder={focusedAumField === 'aum_max' ? 
                        intl.formatMessage({ id: 'succession_deals.wizard.steps.match_preferences.aum.max_aum_placeholder' }).replace(/,/g, '') : 
                        intl.formatMessage({ id: 'succession_deals.wizard.steps.match_preferences.aum.max_aum_placeholder' })}
                    />
                  </div>
                  <div className="mt-1 text-xs text-succession-primary">
                    <FormattedMessage id={focusedAumField === 'aum_max' ? 
                      'succession_deals.wizard.steps.match_preferences.aum.numbers_only_help' : 
                      'succession_deals.wizard.steps.match_preferences.aum.click_to_edit'} />
                  </div>
                </div>
              </div>
            )}
          />
        )}

        {/* Average Client Age */}
        <PremiumMatchQuestion
          title={intl.formatMessage({ id: 'succession_deals.wizard.steps.match_preferences.client_age.title' })}
          description={intl.formatMessage({ id: 'succession_deals.wizard.steps.match_preferences.client_age.description' })}
          selection={preferences.avg_age_of_client}
          options={importanceOptions}
          onChange={handleAvgAgeOfClientChange}
          factorLabels={factorLabels}
          finetuneLabel={finetuneLabel}
          showRangeInputs={true}
          rangeInputs={preferences.avg_age_of_client !== '0' && (
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-succession-text-primary mb-2">
                  <FormattedMessage id="succession_deals.wizard.steps.match_preferences.client_age.min_age_label" />
                </label>
                <Input
                  type="text"
                  value={preferences.avg_age_of_client_min || ''}
                  onChange={handleAvgAgeOfClientMinChange}
                  className="px-4 py-3 border-2 border-succession-border rounded-lg shadow-sm focus:ring-2 focus:ring-succession-primary focus:border-succession-primary transition-all duration-200"
                  placeholder={intl.formatMessage({ id: 'succession_deals.wizard.steps.match_preferences.client_age.min_age_placeholder' })}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-succession-text-primary mb-2">
                  <FormattedMessage id="succession_deals.wizard.steps.match_preferences.client_age.max_age_label" />
                </label>
                <Input
                  type="text"
                  value={preferences.avg_age_of_client_max || ''}
                  onChange={handleAvgAgeOfClientMaxChange}
                  className="px-4 py-3 border-2 border-succession-border rounded-lg shadow-sm focus:ring-2 focus:ring-succession-primary focus:border-succession-primary transition-all duration-200"
                  placeholder={intl.formatMessage({ id: 'succession_deals.wizard.steps.match_preferences.client_age.max_age_placeholder' })}
                />
              </div>
            </div>
          )}
        />

        {/* Conditional Affiliation Questions */}
        {company?.has_affiliation && (
            <PremiumMatchQuestion
              title={intl.formatMessage({ id: 'succession_deals.wizard.steps.match_preferences.affiliation_network.title' })}
              description={intl.formatMessage({ id: 'succession_deals.wizard.steps.match_preferences.affiliation_network.description' })}
              selection={preferences.affiliation}
              options={importanceOptions}
              onChange={handleAffiliationChange}
              factorLabels={factorLabels}
              finetuneLabel={finetuneLabel}
            />
          )}

          {company?.allow_user_affiliations && (
            <PremiumMatchQuestion
              title={intl.formatMessage({ id: 'succession_deals.wizard.steps.match_preferences.shared_affiliations.title' })}
              description={intl.formatMessage({ id: 'succession_deals.wizard.steps.match_preferences.shared_affiliations.description' })}
              selection={preferences.affiliations}
              options={importanceOptions}
              onChange={handleAffiliationsChange}
              factorLabels={factorLabels}
              finetuneLabel={finetuneLabel}
            />
          )}
      </div>

      {/* Validation Summary or Success Message */}
      {isPreferencesComplete() && !proximityLoading && proximityMessage !== '0' ? (
        /* Elegant Success Summary */
        <AlertCard
          variant="success"
          title={<FormattedMessage id="succession_deals.wizard.steps.match_preferences.validation.profile_ready_title" />}
          description={<FormattedMessage id="succession_deals.wizard.steps.match_preferences.validation.profile_ready_description" />}
          badges={[
            { 
              label: <FormattedMessage id="succession_deals.wizard.steps.match_preferences.validation.preferences_saved" />, 
            },
            { 
              label: <FormattedMessage id="succession_deals.wizard.steps.match_preferences.validation.ready_to_match" />, 
            },
          ]}
        />
      ) : (
        /* Validation Message */
        <AlertCard
          variant="warning"
          title={<FormattedMessage id="succession_deals.wizard.steps.match_preferences.validation.complete_profile_title" />}
          description={<FormattedMessage id="succession_deals.wizard.steps.match_preferences.validation.complete_profile_description" />}
        >
          {/* Validation Details */}
          <div className="text-left max-w-md mx-auto space-y-2">
              {(!preferences.proximity_max || preferences.proximity_max <= 0) && (
                <div className="flex items-center space-x-2 text-succession-text-primary">
                  <span className="text-sm">
                    <FormattedMessage id="succession_deals.wizard.steps.match_preferences.validation.required_steps.set_geographic_range" />
                  </span>
                </div>
              )}
              {proximityMessage === '0' && (
                <div className="flex items-center space-x-2 text-succession-text-primary">
                  <span className="text-sm">
                    <FormattedMessage id="succession_deals.wizard.steps.match_preferences.validation.required_steps.no_matches_increase_distance" />
                  </span>
                </div>
              )}
              {proximityLoading && (
                <div className="flex items-center space-x-2 text-succession-text-primary">
                  <span className="text-sm">
                    <FormattedMessage id="succession_deals.wizard.steps.match_preferences.validation.required_steps.searching_matches" />
                  </span>
                </div>
              )}
              {(!preferences.practice_similarity || preferences.practice_similarity === null) && (
                <div className="flex items-center space-x-2 text-succession-text-primary">
                  <span className="text-sm">
                    <FormattedMessage id="succession_deals.wizard.steps.match_preferences.validation.required_steps.answer_practice_mix" />
                  </span>
                </div>
              )}
              {(!preferences.practice_structure || preferences.practice_structure === null) && (
                <div className="flex items-center space-x-2 text-succession-text-primary">
                  <span className="text-sm">
                    <FormattedMessage id="succession_deals.wizard.steps.match_preferences.validation.required_steps.answer_practice_structure" />
                  </span>
                </div>
              )}
              {(!preferences.servicing || preferences.servicing === null) && (
                <div className="flex items-center space-x-2 text-succession-text-primary">
                  <span className="text-sm">
                    <FormattedMessage id="succession_deals.wizard.steps.match_preferences.validation.required_steps.answer_client_demographics" />
                  </span>
                </div>
              )}
              {(!preferences.length_of_service || preferences.length_of_service === null) && (
                <div className="flex items-center space-x-2 text-succession-text-primary">
                  <span className="text-sm">
                    <FormattedMessage id="succession_deals.wizard.steps.match_preferences.validation.required_steps.answer_experience_level" />
                  </span>
                </div>
              )}
              {/* Length of Service range validation */}
              {preferences.length_of_service && preferences.length_of_service !== '0' && (preferences.length_of_service_min === null || preferences.length_of_service_min === undefined || preferences.length_of_service_max === null || preferences.length_of_service_max === undefined) && (
                <div className="flex items-center space-x-2 text-succession-text-primary">
                  <span className="text-sm">
                    <FormattedMessage id="succession_deals.wizard.steps.match_preferences.validation.required_steps.complete_experience_range" />
                  </span>
                </div>
              )}
              {company?.business_type !== 'p_and_c' && (!preferences.aum || preferences.aum === null) && (
                <div className="flex items-center space-x-2 text-succession-text-primary">
                  <span className="text-sm">
                    <FormattedMessage id="succession_deals.wizard.steps.match_preferences.validation.required_steps.answer_aum" />
                  </span>
                </div>
              )}
              {/* AUM range validation */}
              {company?.business_type !== 'p_and_c' && preferences.aum && preferences.aum !== '0' && (preferences.aum_min === null || preferences.aum_min === undefined || preferences.aum_max === null || preferences.aum_max === undefined) && (
                <div className="flex items-center space-x-2 text-succession-text-primary">
                  <span className="text-sm">
                    <FormattedMessage id="succession_deals.wizard.steps.match_preferences.validation.required_steps.complete_aum_range" />
                  </span>
                </div>
              )}
              {(!preferences.avg_age_of_client || preferences.avg_age_of_client === null) && (
                <div className="flex items-center space-x-2 text-succession-text-primary">
                  <span className="text-sm">
                    <FormattedMessage id="succession_deals.wizard.steps.match_preferences.validation.required_steps.answer_client_age" />
                  </span>
                </div>
              )}
              {/* Average Age of Client range validation */}
              {preferences.avg_age_of_client && preferences.avg_age_of_client !== '0' && (preferences.avg_age_of_client_min === null || preferences.avg_age_of_client_min === undefined || preferences.avg_age_of_client_max === null || preferences.avg_age_of_client_max === undefined) && (
                <div className="flex items-center space-x-2 text-succession-text-primary">
                  <span className="text-sm">
                    <FormattedMessage id="succession_deals.wizard.steps.match_preferences.validation.required_steps.complete_client_age_range" />
                  </span>
                </div>
              )}
              {company?.has_affiliation && (!preferences.affiliation || preferences.affiliation === null) && (
                <div className="flex items-center space-x-2 text-succession-text-primary">
                  <span className="text-sm">
                    <FormattedMessage id="succession_deals.wizard.steps.match_preferences.validation.required_steps.answer_affiliation_network" />
                  </span>
                </div>
              )}
              {company?.allow_user_affiliations && (!preferences.affiliations || preferences.affiliations === null) && (
                <div className="flex items-center space-x-2 text-succession-text-primary">
                  <span className="text-sm">
                    <FormattedMessage id="succession_deals.wizard.steps.match_preferences.validation.required_steps.answer_shared_affiliations" />
                  </span>
                </div>
              )}
            </div>
        </AlertCard>
      )}
    </div>
  );
};

MatchPreferences.propTypes = {
  formData: PropTypes.object.isRequired,
  updateFormData: PropTypes.func.isRequired,
  errors: PropTypes.object,
  currentUser: PropTypes.object,
  company: PropTypes.object
};

export default MatchPreferences; 